import React, {
    useRef,
    useState,
    useMemo,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import _get from 'lodash/get';

import FONTS from '@mlb/react-patterns/style/fonts.ts';
import COLORS from '@mlb/react-patterns/style/universal.color';
import Button from '@mlb/react-patterns/components/Button/button.tsx';

import { getDeviceInfo } from 'bdvp-playback-page/src/components/Player/utils';
import { Tracking } from 'bdvp-tracking';
import { useClipTrimming } from 'bdvp-common/src/hooks';

import { round } from './utils';
import Wrapper from '../ModalViewWrapper';
import BodyComponent from '../ModalBody';
import Footer from '../ModalFooter';
import Controls from './Controls';
import Clock from './Clock';
import Player from './Player';
import Slider from './Slider';
import TrimInfo from './TrimInfo';

const Body = styled(BodyComponent)`
    padding-top: 0;
    display: flex;
    flex-direction: column;
`;

const Instructions = styled.span`
    display: block;
    margin-bottom: 11px;
    text-align: center;
    color: ${COLORS.mlbGrey2};
    font-family: ${FONTS.primaryFont};
    font-size: 14px;
    width: 100%;
    align-self: flex-start;
`;

const PlayerContent = styled.div`
    display: flex;
    flex-direction: column;
    margin: 15px 0 0 0;
    align-self: flex-end;
    width: 100%;
`;

const ControlsWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`;

const Btn = styled(Button)`
    background-color: ${COLORS.linkBlue};
    min-width: 174px;
`;

const STEP = 0.1;
const getRoundedSegment = (segment) => round(segment, STEP);

const ClipTrimming = (props) => {

    const {
        config,
        mediaPlayback,
        trimClip,
        reelClip,
        submitButtonRef,
        dictionary,
    } = props;

    const { segments } = useClipTrimming(mediaPlayback, config);

    const roundedSegments = useMemo(() => segments.map((segment) => getRoundedSegment(segment)), [segments]);

    const [
        playhead,
        setPlayhead,
    ] = useState(
        segments.indexOf(reelClip.offsetStartTime) !== -1
            ? roundedSegments[segments.indexOf(reelClip.offsetStartTime)]
            : segments[0]
    );

    const [
        playerState,
        setPlayerState,
    ] = useState({
        paused: true,
        volume: 1,
        muted: false,
        lastVolume: 1,
    });

    const [
        mediaPlayer,
        setMediaPlayer,
    ] = useState(null);

    const [
        startTime,
        setStartTime,
    ] = useState(
        segments.indexOf(reelClip.offsetStartTime) !== -1
            ? roundedSegments[segments.indexOf(reelClip.offsetStartTime)]
            : roundedSegments[0]
    );

    const [
        endTime,
        setEndTime,
    ] = useState(
        segments.indexOf(reelClip.offsetEndTime) !== -1
            ? roundedSegments[segments.indexOf(reelClip.offsetEndTime)]
            : roundedSegments[roundedSegments.length - 1]
    );

    const [
        wasPlaying,
        setWasPlaying,
    ] = useState(false);

    const isSliding = useRef(false);

    const deviceInfo = useMemo(() => getDeviceInfo(), []);

    const isShowingTicks = segments.length < (_get(config, 'reels.creation.maxTickCount', 20));

    return (
        <Wrapper>
            <Body>
                <Instructions>
                    {dictionary.trim_instructions || 'Slide the two handles and then select Save Video.'}
                </Instructions>
                <Player
                    config={config}
                    mediaPlayback={mediaPlayback}
                    setMediaPlayer={setMediaPlayer}
                    setPlayhead={setPlayhead}
                    setPlayerState={setPlayerState}
                    startTime={startTime}
                    endTime={endTime}
                    wasPlaying={wasPlaying}
                    playerState={playerState}
                    deviceInfo={deviceInfo}
                    playhead={playhead}
                    step={STEP}
                    segments={segments}
                    roundedSegments={roundedSegments}
                    getRoundedSegment={getRoundedSegment}
                    isSliding={isSliding}
                />
                <PlayerContent>
                    <ControlsWrapper>
                        <Controls
                            mediaPlayer={mediaPlayer}
                            setWasPlaying={setWasPlaying}
                            deviceInfo={deviceInfo}
                            {...playerState}
                        />
                        <Clock
                            loading={!mediaPlayer}
                            dictionary={dictionary}
                            startTime={startTime}
                            endTime={endTime}
                        />
                    </ControlsWrapper>
                    <Slider
                        startTime={startTime}
                        endTime={endTime}
                        playhead={playhead}
                        setPlayhead={setPlayhead}
                        originalDuration={reelClip.originalDuration}
                        wasPlaying={wasPlaying}
                        mediaPlayer={mediaPlayer}
                        setStartTime={setStartTime}
                        setEndTime={setEndTime}
                        segments={roundedSegments}
                        step={STEP}
                        deviceInfo={deviceInfo}
                        isSliding={isSliding}
                        isShowingTicks={isShowingTicks}
                        getRoundedSegment={getRoundedSegment}
                    />
                    {isShowingTicks && <TrimInfo dictionary={dictionary}/>}
                </PlayerContent>
            </Body>
            <Footer>
                <Btn
                    ref={submitButtonRef}
                    label={dictionary.save_video || 'Save Video'}
                    size={'md'}
                    styleName={'link-blue'}
                    handlers={{
                        onClick: () => {

                            Tracking.trackReelCreationSaveClick();
                            const startTimeIndex = roundedSegments.indexOf(startTime);
                            const endTimeIndex = roundedSegments.indexOf(endTime);
                            trimClip(mediaPlayback.id, segments[startTimeIndex], segments[endTimeIndex]);

                        },
                    }}
                />
            </Footer>
        </Wrapper>
    );

};

ClipTrimming.propTypes = {
    config: PropTypes.object.isRequired,
    dictionary: PropTypes.object.isRequired,
    trimClip: PropTypes.func.isRequired,
    mediaPlayback: PropTypes.shape({
        id: PropTypes.string.isRequired,
    }).isRequired,
    reelClip: PropTypes.shape({
        offsetStartTime: PropTypes.number.isRequired,
        offsetEndTime: PropTypes.number.isRequired,
        originalDuration: PropTypes.number.isRequired,
    }).isRequired,
    submitButtonRef: PropTypes.object.isRequired,
};

export default ClipTrimming;
